import { Component } from '@angular/core';

@Component({
    selector: 'technical-operational-closure',
    template: 
    `
    <pet-page-template [data]='pageData' [additionalContent]='additionalContent' [status]='"Draft"'></pet-page-template>
    `,
    styles: ``
  })
export class TechOpClosureComponent {
    public pageData: any = [

        {
          type: 'intro',
          value: {
            mainText: 'Introduction',
            subText:
              'Technical and Operational Closure focuses on cleanly winding down a product after customer transition is complete. The objective is to properly decommission code, infrastructure, integrations, access, and dependencies while ensuring data retention and compliance requirements are fully met. This phase prevents “ghost systems,” reduces ongoing risk, and ensures the product exits the organisation’s landscape cleanly and safely.'
          }
        },
    
        {
          type: 'tileItem',
          value: {
            title: 'Roles and Responsibilities',
            items: [
              {
                name: 'Tech Lead',
                description: 'Owns the technical closure strategy, ensures that decommissioning steps are safe, traceable, and aligned with architectural and security standards.'
              },
              {
                name: 'Engineering Team',
                description: 'Executes closure tasks such as code archival, removing pipelines, shutting down services, and ensuring security of remaining artifacts.'
              },
              {
                name: 'Security & Compliance',
                description: 'Advises on data retention, access cleanup, audit requirements, and ensures technical shutdown aligns with regulatory and corporate standards.'
              },
              {
                name: 'Cloud Operations',
                description: 'Handles decommissioning of environments, infrastructure resources, monitoring, CI/CD pipelines, and deployment targets.'
              },
              {
                name: 'Support',
                description: 'Closes outstanding tickets, archives knowledge base content, and updates internal support documentation to reflect the product’s end of life.'
              },
              {
                name: 'Product Manager',
                description: 'Confirms scope of what must be technically shut down, approves timing, and formally signs off when technical and operational closure is complete, ensuring steps align with customer and commercial commitments.'
              }
            ]
          }
        },
    
        {
          type: 'checkListItem',
          value: {
            title: 'Prerequisites',
            items: [
              { id: 1, text: 'All customer transitions are complete or formally closed with exceptions documented.' },
              { id: 2, text: 'Contracts, obligations, and notice periods have been honoured and recorded.' },
              { id: 3, text: 'A complete list of technical assets, integrations, and dependencies is available (including environments, repos, pipelines, monitoring, keys, and credentials).' },
              { id: 4, text: 'Data retention and compliance requirements are clarified with Security and Legal.' }
            ]
          }
        },
    
        {
          type: "superListItem",
          value: {
            title: "Process Steps",
            items: [
              {
                title: "Prepare Final Release or Freeze",
                description: {
                  "Objective": [
                    "Deliver a stable final version or freeze the codebase to ensure no further changes occur during the shutdown phase."
                  ],
                  "Activity": [
                    "Apply any critical fixes needed before closure if required by customer commitments.",
                    "Tag the final release, freeze the repository, and ensure all pipelines stop producing artifacts.",
                    "Validate that no automated deployments or cron jobs can accidentally trigger new releases."
                  ],
                  "Outcome": [
                    "A final, stable baseline for archival and future reference."
                  ]
                }
              },
              {
                title: "Archive Source Code, Repos, and Artifacts",
                description: {
                  "Objective": [
                    "Ensure code, documentation, and technical artifacts remain accessible for audit, traceability, and historical reference."
                  ],
                  "Activity": [
                    "Archive repositories, branches, documentation, runbooks, and design artifacts in read-only mode.",
                    "Store final builds, release notes, and deployment artifacts in long-term archival storage.",
                    "Remove unnecessary secrets, test data, or residual credentials from repos before archival."
                  ],
                  "Outcome": [
                    "A clean, secure, and fully archived record of the product in a non-active state."
                  ]
                }
              },
              {
                title: "Execute Data Retention and Compliance",
                description: {
                  "Objective": [
                    "Handle all data in compliance with legal, contractual, and corporate retention standards."
                  ],
                  "Activity": [
                    "Identify which data must be retained, anonymised, exported, or deleted (logs, user data, telemetry).",
                    "Coordinate with Legal and Security to meet GDPR, audit, or internal policy requirements.",
                    "Document what was deleted, what was retained, and where long-term storage resides."
                  ],
                  "Outcome": [
                    "A compliant data handling record with confirmed retention and deletion actions."
                  ]
                }
              },
              {
                title: "Shut Down Infrastructure and Deployments",
                description: {
                  "Objective": [
                    "Fully decommission all environments, services, compute resources, and integrations supporting the product."
                  ],
                  "Activity": [
                    "Shut down cloud resources including servers, containers, storage, databases, queues, and scheduled tasks.",
                    "Remove DNS records, load balancers, security groups, and network policies tied to the product.",
                    "Disable monitoring tools, alert rules, dashboards, and logging pipelines related to the product."
                  ],
                  "Outcome": [
                    "No active infrastructure, no cost leakage, and no operational resources tied to the product."
                  ]
                }
              },
              {
                title: "Remove Access, Licenses, and Integrations",
                description: {
                  "Objective": [
                    "Eliminate all unnecessary access, credentials, integrations, and partner connections."
                  ],
                  "Activity": [
                    "Remove API keys, service accounts, OAuth clients, and secrets from vaults or key stores.",
                    "Deactivate third-party integrations, partner connections, and data pipelines.",
                    "Revoke user, developer, and service permissions from code repos and cloud environments."
                  ],
                  "Outcome": [
                    "The product no longer exposes operational risk through lingering access or integrations."
                  ]
                }
              },
              {
                title: "Operational Wrap-Up and Final Documentation",
                description: {
                  "Objective": [
                    "Capture what was done, why it was done, and how compliance and shutdown requirements were fulfilled, and confirm closure with the business owner."
                  ],
                  "Activity": [
                    "Document the entire technical closure process including key actions, risk mitigations, and final state.",
                    "Close or archive support tickets, update internal documentation, and remove product references from portals or knowledge bases.",
                    "Prepare a closure summary for Product, Security, and Compliance teams and review it with the Product Manager for final approval."
                  ],
                  "Outcome": [
                    "A complete closure package, formally approved by the Product Manager, ensuring transparency, traceability, and organisational readiness for audit."
                  ]
                }
              }
            ]
          }
        },
    
        {
          type: 'multibulletList',
          value: {
            title: 'Key Inputs for Technical and Operational Closure',
            items: [
              {
                name: 'Asset Inventory',
                description: [
                  'Description: Full list of repos, services, environments, pipelines, secrets, keys, and integrations.',
                  'Example: A dependency map showing all systems connected to the product including internal APIs and partner systems.'
                ]
              },
              {
                name: 'Data Retention Matrix',
                description: [
                  'Description: What data must be retained, for how long, and in which format, including deletion rules.',
                  'Example: GDPR classification determining which logs must be anonymised or deleted within a defined timeline.'
                ]
              },
              {
                name: 'Security & Compliance Requirements',
                description: [
                  'Description: Required evidence, audit trails, risk mitigations, and documentation obligations.',
                  'Example: SOC 2 requirement to show logs of revoked service accounts for systems being shut down.'
                ]
              },
              {
                name: 'End-State Architecture / Infrastructure Map',
                description: [
                  'Description: A clear view of what will remain active in the environment and what must be fully removed.',
                  'Example: A diagram showing final remaining systems after cleanup and decommission.'
                ]
              },
              {
                name: 'Final Release or Freeze Artifacts',
                description: [
                  'Description: Source code, packages, images, binaries, and documentation stored for future reference.',
                  'Example: Final tagged container image stored in a read-only registry.'
                ]
              }
            ]
          }
        },
    
        {
          type: 'listItem',
          value: {
            title: 'Outcomes of Complete Technical & Operational Closure',
            items: [
              {
                name: 'Zero operational footprint',
                description:
                  'No environments, services, or integrations remain active. Operational cost and security exposure drop to zero.'
              },
              {
                name: 'Full compliance and traceability',
                description:
                  'All data retention, deletion, audit, and regulatory requirements are fulfilled and documented.'
              },
              {
                name: 'Clean organisational handover',
                description:
                  'Teams know the product is fully retired, with no remaining dependencies, responsibilities, or support obligations.'
              },
              {
                name: 'Reduced long-term risk',
                description:
                  'Old credentials, integrations, and infrastructure are eliminated, preventing future vulnerabilities.'
              }
            ]
          }
        }
      ];
    
      public additionalContent = [
        {
          type: 'tips',
          title: 'Tips',
          value:
            'Treat decommissioning like a release: structured, documented, staged, and verified. A clean shutdown prevents hidden cost, lingering risk, and accidental reactivation.'
        },
        {
          type: 'attachments',
          title: 'Templates / Guides',
          value: [
            {
              name: 'Technical Shutdown Checklist (underway)',
              url: '#'
            },
            {
              name: 'Data Retention & Deletion Register (underway)',
              url: '#'
            }
          ]
        },
        {
          type: 'optionalContent',
          title: 'Benefits and Repercussions',
          value: [
            {
              key: 'Benefits',
              data: [
                'Eliminates cost leakage from unused infrastructure or services.',
                'Reduces long-term security exposure by removing old access points and dormant systems.',
                'Improves audit readiness with clean, traceable evidence of product retirement.',
                'Ensures organisational clarity by fully removing the product from operational processes.'
              ]
            },
            {
              key: 'Repercussions',
              data: [
                'Incomplete shutdown can leave behind shadow systems that incur cost or risk.',
                'Missing access removals can create vulnerabilities discovered months or years later.',
                'Failure to meet data retention or deletion rules can result in compliance breaches.',
                'Inadequate documentation makes audits and internal reviews difficult.'
              ]
            }
          ]
        }
      ];
    }